import React from "react";
import {
  MessageSquare, PlusCircle, Building2, TrendingUp, Award, AlertTriangle, Sparkles, CalendarCheck,
} from "lucide-react";
import { toast } from "sonner";
import TopBar from "../components/layout/TopBar";
import LogoUploader from "../components/LogoUploader";
import useApi from "../hooks/useApi";
import { useAuth } from "../context/AuthContext";
import { can } from "../lib/plans";

export default function Account() {
  const { user } = useAuth();
  const group = useApi("/group").data;
  const managed = useApi("/managed").data;
  const isManaged = can(user?.tier || "score", "managed");

  return (
    <>
      <TopBar title="Branch & Account" subtitle="Gilmours" />
      <div className="p-6 sm:p-8 max-w-[1400px] space-y-10">

        <LogoUploader />

        {/* Managed Growth Team */}
        {isManaged && managed && (
          <section>
            <div className="card p-8 relative overflow-hidden animate-rise" style={{ background: "#151515" }}>
              <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full" style={{ background: "rgba(147,192,32,.12)" }} />
              <div className="relative flex flex-col lg:flex-row gap-8">
                <div className="flex-1">
                  <span className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: "#93C020" }}>Growth Team</span>
                  <h2 className="text-2xl font-bold tracking-tight text-white mt-2">Your social is being managed.</h2>
                  <div className="inline-flex items-center gap-2 mt-3 rounded-full px-3 py-1.5" style={{ background: "rgba(147,192,32,.15)" }}>
                    <span className="w-2 h-2 rounded-full" style={{ background: "#93C020" }} />
                    <span className="text-sm font-semibold" style={{ color: "#93C020" }}>{managed.status}</span>
                  </div>
                  <div className="grid grid-cols-4 gap-4 mt-6 max-w-md">
                    {[["Posts", managed.this_week.posts], ["Stories", managed.this_week.stories], ["Reels", managed.this_week.reels], ["Local", managed.this_week.local]].map(([l, v]) => (
                      <div key={l}><div className="text-3xl font-extrabold tracking-tighter text-white">{v}</div><div className="text-xs text-white/50 mt-0.5">{l} planned</div></div>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-6">
                    <button className="btn-primary focus-ring" onClick={() => toast.success("Message sent to your Growth Team")} data-testid="message-team"><MessageSquare size={15} strokeWidth={1.75} /> Message Growth Team</button>
                    <button className="rounded-md px-4 py-2.5 text-sm font-medium text-white border border-white/20 hover:bg-white/10 transition-all focus-ring inline-flex items-center gap-2" onClick={() => toast.success("Content request submitted")} data-testid="request-content"><PlusCircle size={15} strokeWidth={1.75} /> Request content</button>
                  </div>
                </div>
                <div className="lg:w-72 rounded-xl p-5" style={{ background: "rgba(255,255,255,.05)", border: "1px solid rgba(255,255,255,.1)" }}>
                  <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/50">Next action</span>
                  <div className="flex items-center gap-2 mt-3"><CalendarCheck size={18} color="#93C020" strokeWidth={1.75} /><span className="font-semibold text-white">{managed.next_action.title}</span></div>
                  <p className="text-sm text-white/50 mt-1">{managed.next_action.when}</p>
                  <div className="mt-4 pt-4 border-t border-white/10 space-y-2">
                    {managed.stories.map((s) => (
                      <div key={s.id} className="text-sm">
                        <div className="flex items-center gap-2 text-white"><Sparkles size={13} color="#93C020" /> {s.title}</div>
                        <p className="text-xs text-white/40 ml-5">{s.type} — Growth team will publish manually</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Multi-branch group view */}
        <section>
          <div className="flex items-baseline gap-3 mb-4">
            <h2 className="text-xl font-bold tracking-tight text-ink">Branch performance</h2>
            <span className="text-sm text-ink-2">Group overview</span>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {[
              { l: "Average Growth Score", v: group?.average, icon: Building2 },
              { l: "Best branch", v: group?.best, icon: Award },
              { l: "Fastest growing", v: group?.fastest_growing, icon: TrendingUp },
              { l: "Needs attention", v: group?.needs_attention, icon: AlertTriangle },
            ].map((s) => (
              <div key={s.l} className="card p-5"><s.icon size={17} strokeWidth={1.5} color="#678A16" /><div className="text-2xl font-extrabold tracking-tighter text-ink mt-2 truncate">{s.v}</div><div className="text-xs text-ink-2 mt-0.5">{s.l}</div></div>
            ))}
          </div>
          <div className="card p-2">
            {(group?.branches || []).map((b, i) => (
              <div key={b.id} className="flex items-center gap-4 px-4 py-3" data-testid={`group-branch-${b.id}`}>
                <span className="w-6 text-center font-bold text-ink-3 tabular-nums">{i + 1}</span>
                <span className="flex-1 font-medium text-ink">{b.name}</span>
                <span className="w-40 hidden sm:block h-1.5 rounded-full overflow-hidden" style={{ background: "#EDEEE9" }}>
                  <span className="block h-full rounded-full" style={{ width: `${b.current_score}%`, background: b.current_score >= 75 ? "#93C020" : b.current_score >= 65 ? "#B7CE5E" : "#E5A93D" }} />
                </span>
                <span className="w-10 text-right font-bold text-ink tabular-nums">{b.current_score}</span>
              </div>
            ))}
          </div>
        </section>
      </div>
    </>
  );
}
